// ProfileEditor.jsx — the ONE shared profile editor.
// Used by: desktop My Profile network settings (App.jsx) and the mobile
// MemberProfileScreen (MobileApp.jsx). Both surfaces edit the same
// people_profiles / people_private rows, so they share one component.
//
// Save model (unified — no per-section Save buttons):
//   · Text fields: controlled inputs + debounced auto-save (900ms after last keystroke)
//   · Toggles / skills / photo: save immediately
//   · One status chip ("Saving… / Saved / Couldn't save") for the whole editor
//
// The parent owns the data flow: it passes `profile` + `privateData` and the
// save callbacks. This keeps RLS paths surface-specific (TM claimed-profile
// writes vs. member self-profile writes) while the UI stays identical.

import { useState, useEffect, useRef } from "react";

const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

// (module-local — kept in sync with CAT_COLORS in MobileApp.jsx / PROFILE_ROLE_COLORS in App.jsx)
const PROFILE_CAT_COLORS = {
  band: "#b09cbf", crew: "#8faabc", tm: "#80aaa8", management: "#c4a882",
  production: "#9db89d", session: "#bc8f8f", other: "#9a9490",
};

const IDENTITY_KEYS = ["name", "preferred_name", "role", "bio", "city", "instagram", "email", "phone"];
const TRAVEL_KEYS = [
  "passport_number", "passport_expiry", "passport_country",
  "known_traveler_number", "tsa_precheck", "global_entry",
  "date_of_birth", "nationality",
  "emergency_contact_name", "emergency_contact_phone", "emergency_contact_rel",
  "dietary_restrictions", "medical_notes",
];

// ── Styled switch (replaces native checkboxes for on/off settings) ──────────
export function Switch({ on, onToggle, UI, size = "md", disabled = false }) {
  const w = size === "sm" ? 36 : 44, h = size === "sm" ? 22 : 26, knob = h - 6;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      aria-pressed={!!on}
      style={{
        width: w, height: h, borderRadius: h / 2, border: "none",
        cursor: disabled ? "default" : "pointer", flexShrink: 0,
        background: on ? UI.amber : UI.panelBdr, opacity: disabled ? 0.5 : 1,
        position: "relative", transition: "background 0.2s",
        WebkitTapHighlightColor: "transparent", padding: 0,
      }}>
      <span style={{
        position: "absolute", top: 3, left: on ? w - knob - 3 : 3,
        width: knob, height: knob, borderRadius: "50%",
        background: on ? "#0d0d0d" : UI.textMut, transition: "left 0.2s",
      }} />
    </button>
  );
}

// ── Passport expiry helpers ──────────────────────────────────────────────────
// Accepts legacy free-text ("MM/YYYY", "05/2027") or normalized "YYYY-MM".
function normalizeMonthValue(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\s*\/\s*(\d{4})$/);
  if (m) return `${m[2]}-${String(m[1]).padStart(2, "0")}`;
  const d = s.match(/^(\d{4})-(\d{2})-\d{2}$/); // full date → month
  if (d) return `${d[1]}-${d[2]}`;
  return "";
}
function passportExpiryStatus(v) {
  const norm = normalizeMonthValue(v);
  if (!norm) return null;
  const [y, mo] = norm.split("-").map(Number);
  const endOfMonth = new Date(y, mo, 0, 23, 59, 59); // last day of expiry month
  const now = new Date();
  if (endOfMonth < now) return { level: "expired", label: "Passport expired" };
  const monthsLeft = (endOfMonth - now) / (1000 * 60 * 60 * 24 * 30.4);
  if (monthsLeft < 6) return { level: "soon", label: `Passport expires in ${Math.max(1, Math.round(monthsLeft))} month${Math.round(monthsLeft) === 1 ? "" : "s"} — many countries require 6 months validity` };
  return { level: "ok", label: null };
}

// ── Small SVG icons (no emoji — app convention) ──────────────────────────────
const CheckIcon = ({ color, size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);
const WarnIcon = ({ color, size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
);
const Chevron = ({ open, color }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}><polyline points="9 18 15 12 9 6" /></svg>
);

// ── Save status chip ─────────────────────────────────────────────────────────
function SaveStatus({ status, UI }) {
  if (status === "idle") return null;
  const map = {
    pending: { txt: "Unsaved changes…", col: UI.textMut },
    saving:  { txt: "Saving…", col: UI.textMut },
    saved:   { txt: "Saved", col: UI.success },
    error:   { txt: "Couldn't save — check connection", col: UI.danger },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FF, fontSize: 11, color: m.col }}>
      {status === "saved" && <CheckIcon color={UI.success} />}
      {status === "error" && <WarnIcon color={UI.danger} size={11} />}
      {m.txt}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ProfileEditor({
  profile,             // people_profiles row — may be null before first save (mobile self-create)
  privateData,         // people_private row or null
  onSaveProfile,       // async (patch) => { error } — patch people_profiles
  onSavePrivate,       // async (patch) => { error } — upsert people_private (omit to hide Travel Info)
  uploadPhoto,         // async (file) => { error } — parent uploads + persists photo_url (omit to hide upload)
  UI,
  variant = "desktop", // "desktop" (2-col grids) | "mobile" (1-col)
  rolePlaceholder = "e.g. FOH Engineer, Tour Manager",
  showVisibility = true,
  travelDefaultOpen = false,
}) {
  const isMobile = variant === "mobile";

  // ── Field state (controlled) ───────────────────────────────────────────────
  const initIdentity = (p) => Object.fromEntries(IDENTITY_KEYS.map(k => [k, p?.[k] || ""]));
  const initTravel = (pv) => ({
    ...Object.fromEntries(TRAVEL_KEYS.map(k => [k, pv?.[k] ?? ""])),
    passport_expiry: normalizeMonthValue(pv?.passport_expiry),
    tsa_precheck: !!pv?.tsa_precheck,
    global_entry: !!pv?.global_entry,
  });

  const [fields, setFields]   = useState(() => initIdentity(profile));
  const [skills, setSkills]   = useState(() => profile?.skills || []);
  const [visible, setVisible] = useState(() => profile?.visible_to_network ?? true);
  const [travel, setTravel]   = useState(() => initTravel(privateData));
  const [skillInput, setSkillInput] = useState("");
  const [travelOpen, setTravelOpen] = useState(travelDefaultOpen);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [status, setStatus]   = useState("idle"); // idle | pending | saving | saved | error

  // ── Late-load sync (fixes the clobber race): when the profile row arrives or
  // changes identity, refresh only fields the user hasn't touched.
  const touchedRef   = useRef(new Set());
  const loadedIdRef  = useRef(profile?.id || null);
  const loadedPvRef  = useRef(privateData?.id || null);
  useEffect(() => {
    if (!profile?.id || loadedIdRef.current === profile.id) return;
    loadedIdRef.current = profile.id;
    setFields(f => {
      const next = { ...f };
      IDENTITY_KEYS.forEach(k => { if (!touchedRef.current.has(k)) next[k] = profile[k] || ""; });
      return next;
    });
    if (!touchedRef.current.has("skills"))  setSkills(profile.skills || []);
    if (!touchedRef.current.has("visible")) setVisible(profile.visible_to_network ?? true);
  }, [profile]);
  useEffect(() => {
    if (!privateData?.id || loadedPvRef.current === privateData.id) return;
    loadedPvRef.current = privateData.id;
    setTravel(t => {
      const fresh = initTravel(privateData);
      const next = { ...t };
      TRAVEL_KEYS.forEach(k => { if (!touchedRef.current.has(`t:${k}`)) next[k] = fresh[k]; });
      return next;
    });
  }, [privateData]);

  // ── Debounced auto-save queue ──────────────────────────────────────────────
  const pendingRef = useRef({ profile: {}, private: {} });
  const timerRef   = useRef(null);
  const savedTimerRef = useRef(null);
  const fieldsRef  = useRef(fields);
  useEffect(() => { fieldsRef.current = fields; });

  const flush = async () => {
    timerRef.current = null;
    const pend = pendingRef.current;
    pendingRef.current = { profile: {}, private: {} };
    const hasProf = Object.keys(pend.profile).length > 0;
    const hasPriv = Object.keys(pend.private).length > 0;
    if (!hasProf && !hasPriv) return;

    // Creating a brand-new profile (mobile self-create): a row needs a name,
    // and the first insert should carry everything typed so far.
    if (hasProf && !profile?.id) {
      if (!fieldsRef.current.name.trim()) { setStatus("pending"); return; }
      // NB: people_profiles text columns are NOT NULL DEFAULT '' — always send
      // strings, never null.
      pend.profile = {
        ...Object.fromEntries(IDENTITY_KEYS.map(k => [k, (fieldsRef.current[k] || "").trim()])),
        ...pend.profile,
        name: fieldsRef.current.name.trim(),
        visible_to_network: visible,
      };
    }

    setStatus("saving");
    let err = null;
    if (hasProf) { const r = await onSaveProfile(pend.profile); err = err || r?.error; }
    if (hasPriv && onSavePrivate) { const r = await onSavePrivate(pend.private); err = err || r?.error; }
    if (err) {
      // Re-queue failed payloads so a retry (next keystroke or toggle) resends them.
      pendingRef.current = {
        profile: { ...pend.profile, ...pendingRef.current.profile },
        private: { ...pend.private, ...pendingRef.current.private },
      };
      setStatus("error");
    } else {
      setStatus("saved");
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus(s => (s === "saved" ? "idle" : s)), 2200);
    }
  };
  const flushRef = useRef(null);
  useEffect(() => { flushRef.current = flush; });

  const queue = (kind, patch, delay = 900) => {
    pendingRef.current[kind] = { ...pendingRef.current[kind], ...patch };
    setStatus("pending");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current(), delay);
  };

  // Flush pending work on unmount (best effort).
  useEffect(() => () => {
    clearTimeout(savedTimerRef.current);
    if (timerRef.current) { clearTimeout(timerRef.current); flushRef.current(); }
  }, []);

  // ── Change handlers ────────────────────────────────────────────────────────
  const setIdentityField = (k, v) => {
    touchedRef.current.add(k);
    setFields(f => ({ ...f, [k]: v }));
    // people_profiles text columns are NOT NULL DEFAULT '' — send the raw string.
    queue("profile", { [k]: v });
  };
  const setTravelField = (k, v) => {
    touchedRef.current.add(`t:${k}`);
    setTravel(t => ({ ...t, [k]: v }));
    queue("private", { [k]: v }, typeof v === "boolean" ? 250 : 900);
  };
  const toggleVisible = () => {
    touchedRef.current.add("visible");
    const next = !visible;
    setVisible(next);
    queue("profile", { visible_to_network: next }, 250);
  };
  const saveSkills = (next) => {
    touchedRef.current.add("skills");
    setSkills(next);
    queue("profile", { skills: next }, 250);
  };
  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    setSkillInput("");
    if (!skills.includes(s)) saveSkills([...skills, s]);
  };
  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadPhoto) return;
    setPhotoUploading(true);
    setStatus("saving");
    const r = await uploadPhoto(file);
    setPhotoUploading(false);
    setStatus(r?.error ? "error" : "saved");
    if (!r?.error) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus(s => (s === "saved" ? "idle" : s)), 2200);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inp = {
    width: "100%", boxSizing: "border-box", fontFamily: FF,
    fontSize: isMobile ? 14 : 12, background: isMobile ? UI.panelAlt : UI.rowBg,
    border: `1px solid ${isMobile ? UI.panelBdr : UI.fieldBdr}`,
    borderRadius: isMobile ? 8 : 4, padding: isMobile ? "10px 12px" : "7px 10px",
    color: UI.text, outline: "none", WebkitAppearance: "none",
  };
  const lbl = {
    fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
    color: UI.textMut, marginBottom: isMobile ? 6 : 4, display: "block",
  };
  const subHdr = {
    fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase",
    color: UI.textFnt, marginBottom: 8,
  };
  const grid2 = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 14 : 10 };
  const span2 = isMobile ? {} : { gridColumn: "span 2" };

  const catColor  = PROFILE_CAT_COLORS[profile?.role_category] || UI.textFnt;
  const initials  = (fields.preferred_name || fields.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const expiryWarn = passportExpiryStatus(travel.passport_expiry);
  const isNew = !profile?.id;

  return (
    <div>
      {/* ── Photo + save status ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div style={{
          width: isMobile ? 56 : 64, height: isMobile ? 56 : 64, borderRadius: "50%",
          background: `${catColor}22`, border: `2px solid ${catColor}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden", flexShrink: 0,
          fontFamily: FF, fontSize: isMobile ? 17 : 20, fontWeight: 700, color: catColor,
        }}>
          {profile?.photo_url
            ? <img src={profile.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {uploadPhoto && (
            <>
              <label style={{
                display: "inline-block", cursor: "pointer", fontFamily: FF, fontSize: 11,
                color: UI.textMut, background: "none", border: `1px solid ${UI.fieldBdr}`,
                borderRadius: 4, padding: "5px 12px",
              }}>
                {photoUploading ? "Uploading…" : profile?.photo_url ? "Change Photo" : "Upload Photo"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} disabled={photoUploading || isNew} />
              </label>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 5 }}>
                {isNew ? "Add your name first to enable photo upload" : "JPG, PNG · Max 5MB"}
              </div>
            </>
          )}
        </div>
        <div style={{ flexShrink: 0 }}><SaveStatus status={status} UI={UI} /></div>
      </div>

      {/* ── Identity fields ── */}
      <div style={{ ...grid2, marginBottom: 16 }}>
        <div style={span2}>
          <label style={lbl}>Full Name</label>
          <input value={fields.name} onChange={e => setIdentityField("name", e.target.value)}
            placeholder="Your name" autoCapitalize="words" autoComplete="name" style={inp} />
          {isNew && !fields.name.trim() && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 4 }}>
              Your profile is created once you add your name.
            </div>
          )}
        </div>
        <div>
          <label style={lbl}>Goes By <span style={{ color: UI.textVFnt, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
          <input value={fields.preferred_name} onChange={e => setIdentityField("preferred_name", e.target.value)}
            placeholder="Nickname or preferred name" autoCapitalize="words" style={inp} />
        </div>
        <div>
          <label style={lbl}>Role</label>
          <input value={fields.role} onChange={e => setIdentityField("role", e.target.value)}
            placeholder={rolePlaceholder} autoCapitalize="words" style={inp} />
        </div>
        <div style={span2}>
          <label style={lbl}>Short Bio</label>
          <textarea value={fields.bio} onChange={e => setIdentityField("bio", e.target.value)}
            rows={3} placeholder="A sentence or two about yourself" style={{ ...inp, resize: "vertical" }} />
        </div>
        <div>
          <label style={lbl}>City</label>
          <input value={fields.city} onChange={e => setIdentityField("city", e.target.value)}
            placeholder="Where you're based" style={inp} />
        </div>
        <div>
          <label style={lbl}>Instagram</label>
          <input value={fields.instagram} onChange={e => setIdentityField("instagram", e.target.value)}
            placeholder="@handle" autoCapitalize="none" style={inp} />
        </div>
        <div>
          <label style={lbl}>Contact Email</label>
          <input value={fields.email} onChange={e => setIdentityField("email", e.target.value)}
            placeholder="Shown to connections" type="email" autoCapitalize="none" style={inp} />
        </div>
        <div>
          <label style={lbl}>Phone</label>
          <input value={fields.phone} onChange={e => setIdentityField("phone", e.target.value)}
            placeholder="+1 555.000.0000" type="tel" style={inp} />
        </div>
      </div>

      {/* ── Skills ── */}
      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Skills / Tags</label>
        {skills.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {skills.map(s => (
              <span key={s} style={{
                fontFamily: FF, fontSize: 11, background: isMobile ? UI.panelAlt : UI.rowBg,
                border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 8px",
                display: "inline-flex", alignItems: "center", gap: 6, color: UI.text,
              }}>
                {s}
                <button onClick={() => saveSkills(skills.filter(x => x !== s))} aria-label={`Remove ${s}`}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={UI.danger} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          value={skillInput}
          onChange={e => setSkillInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
          onBlur={addSkill}
          placeholder="Add skill, press Enter" disabled={isNew}
          style={{ ...inp, opacity: isNew ? 0.5 : 1 }} />
      </div>

      {/* ── Visibility ── */}
      {showVisibility && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
          padding: "14px 0", borderTop: `1px solid ${UI.panelBdr}`,
          borderBottom: onSavePrivate ? "none" : `1px solid ${UI.panelBdr}`,
        }}>
          <div>
            <div style={{ fontFamily: FF, fontSize: isMobile ? 14 : 12, fontWeight: 600, color: UI.text }}>Visible to Network</div>
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
              {visible ? "Connections can find you and see when you're in town" : "Hidden from the network directory and city searches"}
            </div>
          </div>
          <Switch on={visible} onToggle={toggleVisible} UI={UI} disabled={isNew} />
        </div>
      )}

      {/* ── Travel Info (people_private) ── */}
      {onSavePrivate && !isNew && (
        <div style={{ borderTop: `1px solid ${UI.panelBdr}` }}>
          <button onClick={() => setTravelOpen(o => !o)}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, padding: "14px 0", background: "none", border: "none", cursor: "pointer",
              textAlign: "left", WebkitTapHighlightColor: "transparent",
            }}>
            <div>
              <div style={{ fontFamily: FF, fontSize: isMobile ? 14 : 12, fontWeight: 600, color: UI.text }}>Travel Info</div>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
                Passport, TSA, and emergency info — visible only to your tour manager
              </div>
            </div>
            <Chevron open={travelOpen} color={UI.textFnt} />
          </button>
          {expiryWarn && expiryWarn.level !== "ok" && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 10,
              background: expiryWarn.level === "expired" ? UI.dangerBg : UI.warningBg,
              border: `1px solid ${expiryWarn.level === "expired" ? UI.dangerBdr : UI.warningBdr}`,
              borderRadius: 6,
            }}>
              <WarnIcon color={expiryWarn.level === "expired" ? UI.danger : UI.warning} />
              <span style={{ fontFamily: FF, fontSize: 11, color: expiryWarn.level === "expired" ? UI.danger : UI.warning }}>
                {expiryWarn.label}
              </span>
            </div>
          )}
          {travelOpen && (
            <div style={{ paddingBottom: 16 }}>
              <div style={subHdr}>Passport</div>
              <div style={{ ...grid2, marginBottom: 16 }}>
                <div>
                  <label style={lbl}>Passport Number</label>
                  <input value={travel.passport_number} onChange={e => setTravelField("passport_number", e.target.value)}
                    autoCapitalize="characters" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Expiry</label>
                  <input type="month" value={travel.passport_expiry}
                    onChange={e => setTravelField("passport_expiry", e.target.value)} style={inp} />
                </div>
                <div style={span2}>
                  <label style={lbl}>Issuing Country</label>
                  <input value={travel.passport_country} onChange={e => setTravelField("passport_country", e.target.value)} style={inp} />
                </div>
              </div>

              <div style={subHdr}>Travel Programs</div>
              <div style={{ ...grid2, marginBottom: 16 }}>
                <div style={span2}>
                  <label style={lbl}>Known Traveler Number (KTN)</label>
                  <input value={travel.known_traveler_number} onChange={e => setTravelField("known_traveler_number", e.target.value)} style={inp} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Switch size="sm" on={!!travel.tsa_precheck} onToggle={() => setTravelField("tsa_precheck", !travel.tsa_precheck)} UI={UI} />
                  <span style={{ fontFamily: FF, fontSize: 12, color: UI.text }}>TSA PreCheck</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Switch size="sm" on={!!travel.global_entry} onToggle={() => setTravelField("global_entry", !travel.global_entry)} UI={UI} />
                  <span style={{ fontFamily: FF, fontSize: 12, color: UI.text }}>Global Entry</span>
                </div>
              </div>

              <div style={subHdr}>Personal</div>
              <div style={{ ...grid2, marginBottom: 16 }}>
                <div>
                  <label style={lbl}>Date of Birth</label>
                  <input type="date" value={travel.date_of_birth} onChange={e => setTravelField("date_of_birth", e.target.value)} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Nationality</label>
                  <input value={travel.nationality} onChange={e => setTravelField("nationality", e.target.value)} style={inp} />
                </div>
              </div>

              <div style={subHdr}>Emergency Contact</div>
              <div style={{ ...grid2, marginBottom: 16 }}>
                <div style={span2}>
                  <label style={lbl}>Name</label>
                  <input value={travel.emergency_contact_name} onChange={e => setTravelField("emergency_contact_name", e.target.value)}
                    autoCapitalize="words" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Phone</label>
                  <input type="tel" value={travel.emergency_contact_phone} onChange={e => setTravelField("emergency_contact_phone", e.target.value)} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Relationship</label>
                  <input value={travel.emergency_contact_rel} onChange={e => setTravelField("emergency_contact_rel", e.target.value)}
                    placeholder="e.g. Spouse, Parent" style={inp} />
                </div>
              </div>

              <div style={subHdr}>Health &amp; Dietary</div>
              <div style={grid2}>
                <div>
                  <label style={lbl}>Dietary Restrictions</label>
                  <input value={travel.dietary_restrictions} onChange={e => setTravelField("dietary_restrictions", e.target.value)}
                    placeholder="e.g. Vegetarian, Nut allergy" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Medical Notes</label>
                  <input value={travel.medical_notes} onChange={e => setTravelField("medical_notes", e.target.value)}
                    placeholder="e.g. Epipen, Asthma inhaler" style={inp} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
